/**
 * A memo of the last draft order-set created for each ordering context.
 *
 * Rapid "Add to cart" clicks call the add-variants mutation before React has
 * re-rendered with a newly created order-set id, so without this every caller
 * would create its own draft. Remembering the id here rather than in a hook ref
 * makes it survive remounts and reach every catalog page at once.
 *
 * The mutation's `scope` already serializes adds within one ordering context, so
 * the entry is usually settled by the time it is read; awaiting it covers adds
 * that are not in a single scope. Callers supply the cache lookups; this module
 * owns only the map.
 */

const orderSetIdByDraftContext = new Map<string, Promise<string>>();

type ResolveDraftOrderSetIdOptions = {
  /** Identifies the ordering context a draft belongs to. */
  key: string;
  /** The draft already known to the query cache, if any. */
  findCachedDraftId: () => string | undefined;
  /** False once a previously resolved draft is submitted, deleted, or gone. */
  isDraftStillUsable: (orderSetId: string) => boolean;
  createDraft: () => Promise<string>;
};

export const resolveDraftOrderSetId = async ({
  key,
  findCachedDraftId,
  isDraftStillUsable,
  createDraft,
}: ResolveDraftOrderSetIdOptions): Promise<string> => {
  const cachedId = findCachedDraftId();
  if (cachedId) return cachedId;

  const pendingId = orderSetIdByDraftContext.get(key);
  if (pendingId) {
    const resolvedId = await pendingId;
    if (isDraftStillUsable(resolvedId)) return resolvedId;
    // Only clear the entry we just judged unusable; a later add may have
    // already replaced it with a newer create.
    if (orderSetIdByDraftContext.get(key) === pendingId) {
      orderSetIdByDraftContext.delete(key);
    }
    return resolveDraftOrderSetId({
      key,
      findCachedDraftId,
      isDraftStillUsable,
      createDraft,
    });
  }

  const createPromise = createDraft().catch((err) => {
    orderSetIdByDraftContext.delete(key);
    throw err;
  });
  orderSetIdByDraftContext.set(key, createPromise);
  return createPromise;
};

/** Test-only: drop remembered drafts so cases do not leak into each other. */
export const resetRememberedDraftOrderSets = () =>
  orderSetIdByDraftContext.clear();
